import type { ProjektMerkmale } from '../../components/planungsphase/types';
import { DOKUMENTE } from './katalog';
import { REGELN } from './regeln';
import type { DokumentVorschlag, LegendenEintrag, MerkmalTreffer, MitgeltendesDokument, SchrittText } from './types';

// Die Signaturen dieser Datei sind der Vertrag mit SiGe-Plan, Planungsphase,
// A+S-Plan und Unterlage fuer spaetere Arbeiten — nicht aendern.

const UMLAUTE: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** Klein, ä→ae ö→oe ü→ue ß→ss, Satzzeichen → Leerzeichen, Mehrfach-Leerzeichen zusammengezogen. */
export const normalisiere = (text: string): string =>
    String(text ?? '')
        .normalize('NFC')
        .toLowerCase()
        .replace(/[äöüß]/g, z => UMLAUTE[z])
        // Alles, was weder Buchstabe noch Ziffer ist (Bindestrich, Schraegstrich,
        // Klammern, Paragraphenzeichen …), trennt Woerter.
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();

// ─── Katalogindex ────────────────────────────────────────────────────────

const DOKUMENT_NACH_ID = new Map<string, MitgeltendesDokument>(DOKUMENTE.map(d => [d.id, d]));

/**
 * Alle Schreibweisen eines Dokuments (Id, Name, Kurzform, Alias) in
 * normalisierter Form. Die Kurzform zaehlt mit, damit ein gespeichertes
 * „AuV“ als dasselbe Dokument erkannt wird wie die Langform.
 */
const schreibweisen = (d: MitgeltendesDokument): string[] =>
    [d.id, d.name, d.kurz, ...(d.alias || [])].filter((s): s is string => !!s).map(normalisiere).filter(Boolean);

const DOKUMENT_NACH_SCHLUESSEL: Map<string, MitgeltendesDokument> = (() => {
    const m = new Map<string, MitgeltendesDokument>();
    DOKUMENTE.forEach(d => schreibweisen(d).forEach(s => { if (!m.has(s)) m.set(s, d); }));
    return m;
})();

/** Katalogdokument zu Id, Name oder Alias (normalisiert verglichen) — sonst undefined (freier Eintrag). */
export const findeDokument = (nameOderId: string): MitgeltendesDokument | undefined => {
    if (nameOderId == null) return undefined;
    const roh = String(nameOderId).trim();
    if (!roh) return undefined;
    return DOKUMENT_NACH_ID.get(roh) ?? DOKUMENT_NACH_SCHLUESSEL.get(normalisiere(roh));
};

// ─── Vorschlaege ─────────────────────────────────────────────────────────

/**
 * Steht das Dokument schon woertlich in einer Massnahme? Name und Alias
 * zaehlen als Teilstring („Sicherheitsdatenblätter und schriftliche
 * Anweisung“ enthaelt die SDB), die Kurzform nur als ganzes Wort — sonst
 * steckte „GB“ in „Umgebung“.
 */
const stehtInMassnahmen = (d: MitgeltendesDokument, massnahmen: string[]): boolean => {
    if (!massnahmen.length) return false;
    const lang = [d.name, ...(d.alias || [])].map(normalisiere).filter(s => s.length >= 4);
    const kurz = d.kurz ? normalisiere(d.kurz) : '';
    return massnahmen.some(m =>
        lang.some(s => m.includes(s)) || (!!kurz && (' ' + m + ' ').includes(' ' + kurz + ' ')));
};

/**
 * Vorschlaege fuer einen Arbeitsschritt: alle Regeln, deren Stichworte in
 * Bezeichnung oder Gewerk treffen. Nie Art C. Ohne Dokumente, die schon in
 * `vorhanden` oder woertlich in `schutzmassnahmen` stehen. Reihenfolge stabil
 * nach Katalogreihenfolge.
 *
 * Loest eine Regel OHNE Bedingung dasselbe Dokument aus wie eine mit
 * Bedingung, gilt das Dokument unbedingt — die Bedingungen entfallen dann,
 * sonst stuende im Editor ein „nur im Bestand“ an einer Abbruchzeile.
 */
export const schlageDokumenteVor = (s: SchrittText): DokumentVorschlag[] => {
    if (!s) return [];
    // Mit Leerzeichen eingerahmt: ein Stamm mit fuehrendem Leerzeichen trifft
    // so auch das erste Wort, aber nie mitten in einem Kompositum.
    const felder = [s.bezeichnung, s.gewerk].map(t => normalisiere(t ?? '')).filter(Boolean).map(f => ' ' + f + ' ');
    if (!felder.length) return [];
    const kommtVor = (stamm: string) => felder.some(f => f.includes(stamm));

    const treffer = new Map<string, { regelIds: string[]; bedingungen: string[]; unbedingt: boolean }>();
    for (const regel of REGELN) {
        if (!regel.stichworte.some(kommtVor)) continue;
        if (regel.undStichworte?.length && !regel.undStichworte.some(kommtVor)) continue;
        if (regel.ausser?.some(kommtVor)) continue;
        for (const id of regel.dokumente) {
            const dok = DOKUMENT_NACH_ID.get(id);
            if (!dok || dok.art === 'C') continue;
            const t = treffer.get(id) ?? { regelIds: [], bedingungen: [], unbedingt: false };
            if (!t.regelIds.includes(regel.id)) t.regelIds.push(regel.id);
            if (!regel.bedingung) t.unbedingt = true;
            else if (!t.bedingungen.includes(regel.bedingung)) t.bedingungen.push(regel.bedingung);
            treffer.set(id, t);
        }
    }
    if (!treffer.size) return [];

    const vorhandeneIds = new Set<string>();
    (s.vorhanden || []).forEach(v => { const d = findeDokument(v); if (d) vorhandeneIds.add(d.id); });
    const massnahmen = (s.schutzmassnahmen || []).map(m => normalisiere(m ?? '')).filter(Boolean);

    return DOKUMENTE
        .filter(d => treffer.has(d.id) && !vorhandeneIds.has(d.id) && !stehtInMassnahmen(d, massnahmen))
        .map(d => {
            const t = treffer.get(d.id)!;
            return { dokument: d, regelIds: t.regelIds, bedingungen: t.unbedingt ? [] : t.bedingungen };
        });
};

/** Namen der Vorschlaege (Langform) — bequem zum direkten Speichern. */
export const vorschlagsNamen = (s: SchrittText): string[] => schlageDokumenteVor(s).map(v => v.dokument.name);

/** Schluessel fuer die Dublettenpruefung: Katalogdokument ueber die Id, freier Eintrag ueber den Text. */
const dublettenSchluessel = (name: string): string => {
    const d = findeDokument(name);
    return d ? 'id:' + d.id : 'frei:' + normalisiere(name);
};

/**
 * Ergaenzt `vorhanden` um `neu`: haengt nur an, entfernt nie, behaelt die
 * Reihenfolge, laesst Dubletten weg (normalisiert, Alias zaehlt als dasselbe
 * Dokument).
 */
export const ergaenzeDokumente = (vorhanden: string[], neu: string[]): string[] => {
    const out = [...(vorhanden || [])];
    const gesehen = new Set(out.map(dublettenSchluessel));
    (neu || []).forEach(n => {
        if (n == null || !normalisiere(n)) return;
        const k = dublettenSchluessel(n);
        if (gesehen.has(k)) return;
        gesehen.add(k);
        out.push(n);
    });
    return out;
};

/** Kurzform fuer die Planspalte, wenn der Katalog eine kennt — sonst der Name unveraendert. */
export const kurzform = (name: string): string => findeDokument(name)?.kurz ?? name;

/** Legendeneintraege fuer die in `namen` verwendeten Kurzformen, ohne Dubletten, sortiert nach Kuerzel. */
export const legendeFuerDokumente = (namen: string[]): LegendenEintrag[] => {
    const nachKuerzel = new Map<string, string>();
    (namen || []).forEach(n => {
        const d = findeDokument(n);
        if (d?.kurz && !nachKuerzel.has(d.kurz)) nachKuerzel.set(d.kurz, d.name);
    });
    return [...nachKuerzel]
        .map(([kuerzel, bedeutung]) => ({ kuerzel, bedeutung }))
        .sort((a, b) => a.kuerzel.localeCompare(b.kuerzel, 'de'));
};

/**
 * Dreiwertige Merkmalsauswertung fuer die Checkliste: ein Merkmal `true` →
 * 'erforderlich'; keines true, aber eines `undefined` → 'zu_klaeren'; alle
 * ausdruecklich `false` → kein Treffer. Dokumente ohne `merkmale` erscheinen
 * nie. Nie Art C.
 */
export const dokumenteFuerMerkmale = (m: ProjektMerkmale): MerkmalTreffer[] => {
    const werte = (m || {}) as Record<string, unknown>;
    const out: MerkmalTreffer[] = [];
    DOKUMENTE.forEach(dokument => {
        if (dokument.art === 'C' || !dokument.merkmale?.length) return;
        const wahr = dokument.merkmale.filter(k => werte[k] === true);
        if (wahr.length) {
            out.push({ dokument, stand: 'erforderlich', merkmale: wahr });
            return;
        }
        const offen = dokument.merkmale.filter(k => werte[k] === undefined || werte[k] === null);
        if (offen.length) out.push({ dokument, stand: 'zu_klaeren', merkmale: offen });
    });
    return out;
};

/** Der eine Satz fuer Art C — steht im Plan einmal (Legende/Grundsatz), nie je Zeile. */
export const GRUNDSATZ_ART_C =
    'Für alle Arbeitsmittel, Gefahrstoffe und Tätigkeiten gelten zusätzlich die allgemeinen Unterlagen des jeweiligen Arbeitgebers: Betriebsanleitung, Betriebsanweisung, Prüfnachweise, Gefährdungsbeurteilung sowie Unterweisungs- und Befähigungsnachweise.';
